import {
  SparklesIcon,
  BuildingStorefrontIcon,
  BoltIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/solid';
import CoreIcon from '../../assets/images/onboarding/plan-icon-core.svg';
import GrowthIcon from '../../assets/images/onboarding/plan-icon-growth.svg';
import ProIcon from '../../assets/images/onboarding/plan-icon-pro.svg';
import ScaleIcon from '../../assets/images/onboarding/plan-icon-scale.svg';

/**
 * Price Plan v2 — the NEW 4-tier lineup (Starter / Growth / Pro / Scale).
 * Single source of truth shared by the in-app Subscription & billing page
 * (NewUserBillingView) and the onboarding plan-selection page (PlanDetailsPage).
 * Text content mirrors `pricing-page - NEW.html` 1:1.
 *
 * Starter ($199) and Growth ($299) map to the Digital Staff credit plans
 * (`ai_starter` / `ai_growth`) so a purchase activates the credit pool. The
 * backend resolves the authoritative price — Price Plan v2 sends no amount.
 */
export type CtaKind = 'trial' | 'upgrade' | 'soon' | 'contact';

/**
 * Pro & Scale are invite-only: their CTA books an early-access call instead of
 * checkout. Shared by `PlanDetailsPage` (onboarding) and `NewUserBillingView`
 * (in-app billing, which also uses it for the platform-trial "Apply for Early
 * Access" links).
 */
export const EARLY_ACCESS_URL = 'https://calendly.com/joanna-chu-yepai/30min';

export interface PlanTier {
  key: string;
  name: string;
  monthly: number;
  /** Promo "was" price shown struck-through before the current one (Growth). */
  was?: number;
  /** Shown with a "From" prefix (Scale). */
  fromPrefix?: boolean;
  Icon: typeof SparklesIcon;
  /** Figma card icon (outline SVG, purple stroke) — used by PlanDetailsPage. */
  iconSrc: string;
  /** backend plan id for the real payment flow (Growth only). */
  realPlanId?: string;
  featured?: boolean;
  cta: CtaKind;
  /** External URL for the 'contact' CTA. */
  contactUrl?: string;
  tagline: string;
  feats: string[];
}

export const PLAN_TIERS: PlanTier[] = [
  {
    key: 'starter',
    name: 'Core', // display name only — backend plan id stays `starter`/enterprise
    monthly: 199,
    Icon: SparklesIcon,
    iconSrc: CoreIcon,
    realPlanId: 'ai_starter', // $199 Digital Staff credit plan (4,000 credits / 1 agent)
    cta: 'upgrade',
    tagline: 'Get a full-time AI marketing specialist for your ecommerce store.',
    feats: [
      '4,000 AI credits / month',
      'Campaign brief generation',
      'Website blog content',
      'Weekly social content',
      'Product description support',
      'Shopify publishing support',
      'Basic brand voice training',
      'Basic reporting',
      'Self-serve onboarding',
      'Cancel anytime, no lock-in',
    ],
  },
  {
    key: 'growth',
    name: 'Growth',
    monthly: 299,
    was: 399,
    Icon: BuildingStorefrontIcon,
    iconSrc: GrowthIcon,
    realPlanId: 'ai_growth', // $299 Digital Staff credit plan (7,000 credits / 2 agents)
    featured: true,
    cta: 'upgrade',
    tagline: 'Get 2 AI employees for marketing and support, built for growing stores.',
    feats: [
      'Everything in Core',
      '7,000 AI credits / month',
      '24/7 Shopify website chat',
      'Pre-sale customer support',
      'Product recommendations',
      'Upsell and cross-sell support',
      'Abandoned cart recovery',
      'Order status and tracking',
      '90+ multilingual support',
      'Standard onboarding',
      'Cancel anytime, no lock-in',
    ],
  },
  {
    key: 'pro',
    name: 'Pro',
    monthly: 599,
    Icon: BoltIcon,
    iconSrc: ProIcon,
    cta: 'soon',
    contactUrl: 'https://calendly.com/joanna-chu-yepai/30min',
    tagline: 'Run a full AI growth team across sales, support, marketing and operations.',
    feats: [
      'Everything in Growth',
      '15,000 AI credits / month',
      'Social chat support for Instagram, Facebook and WhatsApp',
      'Operations automation',
      'Order and logistics follow-up',
      'Advanced analytics and funnel insights',
      'Multi-channel sales playbooks',
      'Priority onboarding',
      'Optional performance pricing',
      'Cancel anytime, no lock-in',
    ],
  },
  {
    key: 'scale',
    name: 'Scale',
    monthly: 1199,
    fromPrefix: true,
    Icon: BuildingOffice2Icon,
    iconSrc: ScaleIcon,
    cta: 'contact',
    contactUrl: 'https://calendly.com/joanna-chu-yepai/30min',
    tagline: 'Build your AI ecommerce workforce across stores, regions and channels.',
    feats: [
      'Everything in Pro',
      '30,000 AI credits / month',
      'Multi-store support',
      'Multi-region support',
      'Klaviyo integration',
      'Meta integration',
      'Email and SMS lifecycle automation',
      'Website builder support',
      'Agency dashboard',
      'Advanced permissions',
      'Dedicated onboarding',
      'Dedicated account manager',
      'Enterprise add-ons available',
      'White-label option available',
      'Cancel anytime, no lock-in',
    ],
  },
];

/** Floor to one decimal, matching the Figma per-day pills (299/30 → $9.9). */
export function perDay(monthly: number): string {
  return (Math.floor((monthly / 30) * 10) / 10).toFixed(1);
}
